import { UI, HStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { useRecoilValue } from 'recoil';

import { FilterModalTypes } from '../../../../../../components/FilterModal';
import { SectionTypes } from '../../../../../../components/FilterModal/filters/Section';
import { productsFiltersAtom } from '../../../../../../store/product/filters';
import { useGlobalProductContext } from '../../contexts';
import { useGlobalProductController } from '../../hooks';

export interface ProductCategoriesNormalized {
  title: string;
  value: string;
}

export const ProductCategories = () => {
  const { appliedFilters } = useRecoilValue(productsFiltersAtom);

  const { assetCategories, loadingCategories, hasSomeLoading, product } = useGlobalProductContext();
  const { handleClearSearchTerm, applyCategoriesFilter } = useGlobalProductController();

  const getCorrectFilter = (): FilterModalTypes.SelectedFiltersProps | undefined => {
    return appliedFilters.find(({ fieldName }) => fieldName === product.categoriesField);
  };

  const getSelectedCategories = (
    correctFilter: FilterModalTypes.SelectedFiltersProps
  ): ProductCategoriesNormalized[] => {
    return assetCategories.filter(({ value }) =>
      (correctFilter.filter as SectionTypes.SelectionProps).includes(value)
    );
  };

  const getCorrectSelectedIndexes = (selectedCategories: ProductCategoriesNormalized[]) => {
    return selectedCategories.map(({ value }) =>
      assetCategories.findIndex((category) => category.value === value)
    );
  };

  const getActiveTabs = (): number[] => {
    const correctFilter = getCorrectFilter();

    if (!correctFilter) {
      return [0];
    }

    const selectedCategories = getSelectedCategories(correctFilter);

    return getCorrectSelectedIndexes(selectedCategories);
  };

  const getCurrentSelectedCategories = (): ProductCategoriesNormalized[] => {
    const correctFilter = getCorrectFilter();

    if (!correctFilter) {
      return [];
    }

    return getSelectedCategories(correctFilter);
  };

  const handleTabPress = (index: number) => {
    handleClearSearchTerm();

    if (index === 0) {
      applyCategoriesFilter(product.categoriesField, []);
      return;
    }

    const currentSelectedCategories = getCurrentSelectedCategories();
    const currentSelectedValues = currentSelectedCategories.map(({ value }) => value);
    let newSelection: string[] = [];

    if (currentSelectedCategories.includes(assetCategories[index])) {
      newSelection = currentSelectedValues.filter(
        (value) => value !== assetCategories[index].value
      );
    } else {
      newSelection = [...currentSelectedValues, assetCategories[index].value];
    }

    applyCategoriesFilter(product.categoriesField, newSelection);
  };

  if (!product.hasCategories) {
    return <></>;
  }

  return (
    <HStack space={Spacing['inline.xxxs']} alignItems="center" maxW="75%">
      <UI.Text variant="semibold">{product.categoriesLabel}:</UI.Text>
      <UI.Tabs
        testId="category-tabs"
        isDisabled={hasSomeLoading}
        isLoading={loadingCategories}
        activeTab={getActiveTabs()}
        variant="secondary"
        onTabPress={handleTabPress}
        tabs={assetCategories}
      />
    </HStack>
  );
};
